import React, { useCallback, useEffect, useState } from 'react';
import Table from '../../../../components/Table';
import SearchInput from '../../../../components/SearchInput/SearchInput';
import styles from './style.module.scss';
import pageHeadingStyles from '../../../../styles/PageHeader.module.scss';
import useCommonStore from '../../../../stores/common';
import useManagerApprovalStore from '../../../../stores/managerApproval';
import { fetchAgentPowerappsWithApprovals } from '../../../../services/managerApproval';
import toast from 'react-hot-toast';

const AgentApprovalList = () => {
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const {
    powerappsWithApprovals,
    searchQuery,
    sortBy,
    sortDirection,
    loading,
    currentPage,
    pageCount,
    itemsPerPage,
    setCurrentPage,
    setSearchQuery,
    setSortBy,
    setSortDirection,
    setLoading,
    setSelectedPowerappId
  } = useManagerApprovalStore();

  const loadData = useCallback(async (page = currentPage) => {
    try {
      setLoading(true);
      const response = await fetchAgentPowerappsWithApprovals({
        page,
        per_page: itemsPerPage
      });
      if (!response.status) {
        toast.error('Failed to load approvals', { duration: 5000 });
      }
    } catch (error) {
      console.error('Error loading approvals:', error);
      toast.error('Error loading approvals', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [setLoading, currentPage, itemsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = [
    {
      headerName: 'DBA',
      field: 'powerapp_dba',
      width: '200px',
      allowSort: true,
    },
    {
      headerName: 'Status',
      field: 'status',
      width: '120px',
      allowSort: true,
      render: (row) => {
        const status = row.status?.toLowerCase() || '';
        let statusClass = '';
        switch (status) {
          case 'pending':
            statusClass = styles.statusPending;
            break;
          case 'approved':
            statusClass = styles.statusApproved;
            break;
          case 'declined':
            statusClass = styles.statusDeclined;
            break;
          default:
            statusClass = '';
        }
        return <span className={statusClass}>{row.status || '-'}</span>;
      }
    },
    {
      headerName: 'Type',
      field: 'action_type',
      width: '150px',
      allowSort: true,
      render: (row) => row.action_type || '-'
    },
    {
      headerName: 'Submitted At',
      field: 'created_at',
      width: '150px',
      allowSort: true,
      render: (row) => {
        const date = new Date(row.created_at);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      headerName: 'Last Updated',
      field: 'updated_at',
      width: '150px',
      allowSort: true,
      render: (row) => {
        const date = new Date(row.updated_at);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
  ];

  const onRowClick = useCallback(
    (row) => {
      // If details are already shown, first hide them to reset state
      if (showDetails) {
        setShowDetails(false);
        // Small delay to ensure state is reset before showing new details
        setTimeout(() => {
          setSelectedPowerappId(row.powerapp_id);
          setShowDetails(true);
        }, 0);
      } else {
        setSelectedPowerappId(row.powerapp_id);
        setShowDetails(true);
      }
    },
    [showDetails, setShowDetails, setSelectedPowerappId],
  );

  const handleSort = useCallback((field) => {
    setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    setSortBy(field);
  }, [sortDirection, setSortDirection, setSortBy]);

  const onChangeSearchInput = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  return (
    <>
      <div className={styles.mobile_header}>
        <div className={styles.mobile_filters}>
          <div className={styles.search_wrapper}>
            <SearchInput
              onChange={onChangeSearchInput}
              searchValue={searchQuery}
            />
          </div>
        </div>
      </div>

      <div className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}>
        <div className={styles.title_row}>
          <div className={pageHeadingStyles.title_contents}>
            <p>My Approval Requests</p>
          </div>
          {!showDetails && (
            <div className={styles.right_side}>
              <div className={styles.filters}>
                <SearchInput
                  onChange={onChangeSearchInput}
                  searchValue={searchQuery}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Table
        layout="fixed"
        noMarginTop
        columns={columns}
        data={powerappsWithApprovals}
        sortBy={sortBy}
        sortColumn={sortDirection}
        onSortClick={handleSort}
        onRowClick={onRowClick}
        enableSkeleton={true}
        dataLoading={loading}
        pagination={{
          currentPage,
          pageCount,
          itemsPerPage,
          onPageChange: (page) => {
            setCurrentPage(page);
            loadData(page);
          }
        }}
      />
    </>
  );
};

export default AgentApprovalList;